// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation, useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { clientLogger } from '@seedplanner/logger';
import type {
  DarkModePreference,
  DateFormat,
  SubscriptionStatusType,
  SubscriptionTierType,
  WeekStartDayType,
  WeightUnit,
} from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
// import FacebookIcon from './FacebookIcon';
// import GoogleIcon from './GoogleIcon';
// import logInWithFb from './FbLogin';
// import loginWithGoogle from './GoogleLogin';
import { useLocation, useNavigate } from 'react-router';
import { useColorContext } from '../../context/ColorModeContext';
import { useLoggedInState } from '../../context/LoggedInContext';
import { type UserContextValue, useUserContext } from '../../context/UserContext';
import {
  // Divider,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '../../lib/base';
import { AccountCircleIcon, ArrowDropDownIcon, DarkMode, LightMode, LogoutIcon } from '../../lib/icons';
import { Link } from '../../lib/link';
// import { useAppOptions } from '../render';
import { LOGOUT_MUTATION } from '../../queries/AccountQueries';
import { SIGNIN_MUTATION } from '../../queries/SignInQueries';
import * as routes from '../../routes';
import type {
  AccountButtonHomeQuery,
  AccountButtonHomeQueryVariables,
  LoginMutation,
  LoginMutationVariables,
  LogoutMutation,
  LogoutMutationVariables,
} from '../../types/__generated__/graphql';
import { ExitDialogButton } from '../common/button';

const StyledLink = styled('a', { shouldForwardProp: (prop) => prop !== 'sx' })(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.contrastText,
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  borderStyle: 'solid',
  borderColor: 'transparent',
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
  },
  whiteSpace: 'nowrap',
}));

// function SocialButton({
//   icon,
//   label,
//   iconBgColor,
//   buttonBgColor,
//   onClick,
//   disabled,
// }: {
//   readonly icon: React.ReactElement;
//   readonly label: string;
//   readonly iconBgColor: string;
//   readonly buttonBgColor: string;
//   readonly onClick: () => void;
//   readonly disabled: boolean;
// }) {
//   const doNothing = () => {
//     // do nothing
//   };
//   return (
//     <Box
//       onClick={disabled ? doNothing : onClick}
//       component="button"
//       sx={{
//         ':hover': disabled
//           ? {}
//           : {
//               transform: 'translateY(-2px)',
//               boxShadow: '0 4px 8px 0 rgba(0,0,0,.25)',
//             },
//         height: '50px',
//         boxShadow: '0 2px 4px 0 rgba(0,0,0,.25)',
//         display: 'flex',
//         border: '1px solid transparent',
//         mb: 1,
//         alignItems: 'center',
//         cursor: disabled ? 'auto' : 'pointer',
//         borderRadius: '4px',
//         transition: 'box-shadow .2s, transform .2s',
//         backgroundColor: disabled ? '#e0e0e0' : buttonBgColor,
//         justifyContent: 'flex-start',
//         padding: 0,
//       }}
//     >
//       <Box
//         sx={{
//           backgroundColor: iconBgColor,
//           width: '48px',
//           height: '100%',
//           display: 'flex',
//           flex: '0 0 auto',
//           justifyContent: 'center',
//           alignItems: 'center',
//           borderTopLeftRadius: (t) => t.shape.borderRadius,
//           borderBottomLeftRadius: (t) => t.shape.borderRadius,
//         }}
//       >
//         {icon}
//       </Box>
//       <Box sx={{ textAlign: 'center', width: '225px', flex: '1 1 auto' }}>
//         <Typography color="white" variant="body1">
//           {label}
//         </Typography>
//       </Box>
//     </Box>
//   );
// }

const ACCOUNT_QUERY = gql`
  query AccountButtonHome {
    user {
      firstname
    }
  }
`;

export default function AccountOrLoginButton() {
  const [login, { loading: loginIsLoading }] = useMutation<LoginMutation, LoginMutationVariables>(SIGNIN_MUTATION);
  const [logout, { loading: logoutIsLoading }] = useMutation<LogoutMutation, LogoutMutationVariables>(LOGOUT_MUTATION);
  const loading = loginIsLoading || logoutIsLoading;
  const { isLoggedIn, setIsLoggedIn } = useLoggedInState();
  const { setUserContext, resetUserContext } = useUserContext();
  const location = useLocation();
  const { mode, toggleColorMode, resetColorMode } = useColorContext();
  const isDarkMode = mode === 'dark';
  const { data, refetch, client } = useQuery<AccountButtonHomeQuery, AccountButtonHomeQueryVariables>(ACCOUNT_QUERY);
  const [signInOpen, setSignInOpen] = React.useState(false);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isLoggedIn) {
      setAnchorEl(event.currentTarget);
    } else {
      setSignInOpen(true);
    }
  };
  const handleEnterAccountButton = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      if (isLoggedIn) {
        setAnchorEl(event.currentTarget);
      } else {
        setSignInOpen(true);
      }
    }
  };
  const handleClickSignUp = () => {
    navigate(routes.SIGNUP);
  };
  const handleOnEnterSignUp = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      navigate(routes.SIGNUP);
    }
  };
  const handleOnEnterSignIn = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      setSignInOpen(true);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const onClickLogoutInner = () => {
    if (logoutIsLoading) {
      return;
    }
    void logout({
      onCompleted: async () => {
        setIsLoggedIn(false);
        resetUserContext();
        resetColorMode();
        await client.clearStore();
        navigate(routes.HOME);
      },
      onError: (err) => {
        enqueueSnackbar(err.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loginFailureMessage, setLoginFailureMessage] = React.useState('');

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(event.target.value);
    setLoginFailureMessage('');
  };
  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(event.target.value);
    setLoginFailureMessage('');
  };

  // If logged in status changes, refetch account info
  React.useEffect(() => {
    if (isLoggedIn) {
      void refetch();
    }
  }, [isLoggedIn, refetch]);

  const onLoginSuccess = (res: UserContextValue) => {
    setEmail('');
    setPassword('');
    setSignInOpen(false);
    setUserContext(res);
    setIsLoggedIn(true);
    // If user was already logged in and on some app page, then don't redirect
    if (!routes.requiresAuth(location?.pathname ?? '')) {
      navigate(routes.DASHBOARD);
    }
  };
  const onLoginFailure = (error: Error, provider: string) => {
    enqueueSnackbar(`Error logging in with ${provider}: ${error.message}`, {
      variant: 'error',
      autoHideDuration: 10000,
    });
    clientLogger.error({ title: `${provider}_login_error`, error });
  };

  const emailLogin = () => {
    void login({
      variables: {
        email,
        password,
      },
      onCompleted: (data) => {
        if (data.login) {
          const dataIn = data.login;
          if (dataIn.success) {
            onLoginSuccess({
              timezone: dataIn.timezone,
              permissions: dataIn.permissions,
              darkModePreference: dataIn.darkModePreference as DarkModePreference,
              preferredWeightUnit: dataIn.preferredWeightUnit as WeightUnit,
              preferredDateFormat: dataIn.preferredDateFormat as DateFormat,
              weekStartPreference: dataIn.weekStartPreference as WeekStartDayType,
              subscriptionStatus: dataIn.subscriptionStatus as SubscriptionStatusType,
              subscriptionTier: dataIn.subscriptionTier as SubscriptionTierType,
              freeTrialEndsAt: dataIn.freeTrialEndsAt ?? null,
              language: dataIn.language,
            });
          } else {
            setLoginFailureMessage('Invalid email or password');
          }
        }
      },
      onError: (error) => {
        onLoginFailure(error, 'email');
      },
    });
  };
  const onCloseDialog = () => {
    setSignInOpen(false);
  };
  const onClickAccount = () => {
    navigate(routes.ACCOUNT);
  };
  const onClickColorMode = () => {
    toggleColorMode();
  };
  const maybeFirstname = data?.user?.firstname;

  return (
    <>
      <Box>
        {isLoggedIn ? (
          <Box display="flex" sx={{ flexDirection: { xs: 'row-reverse', md: 'row' }, color: 'primary.contrastText' }}>
            <Box
              onClick={handleClick}
              onKeyDown={handleEnterAccountButton}
              tabIndex={0}
              sx={{ cursor: 'pointer' }}
              display="flex"
              flexDirection="row"
              flexWrap="nowrap"
              alignItems="center"
            >
              <Avatar
                sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', width: 32, height: 32 }}
                alt={maybeFirstname ?? 'My Account'}
              >
                {maybeFirstname ? maybeFirstname[0] : <AccountCircleIcon fontSize="large" />}
              </Avatar>
              <Typography sx={{ pl: 1, pr: 0, display: { xs: 'none', md: 'initial' } }} variant="body1">
                {maybeFirstname ?? 'My Account'}
              </Typography>
              <ArrowDropDownIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />
            </Box>
            <Box sx={{ flex: '0 0 auto' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <StyledLink
              id="hover-link"
              onClick={handleClickSignUp}
              onKeyDown={handleOnEnterSignUp}
              sx={{ display: { xs: 'none', md: 'block' } }}
              tabIndex={0}
            >
              Sign Up
            </StyledLink>
            <StyledLink id="hover-link" onClick={handleClick} onKeyDown={handleOnEnterSignIn} tabIndex={0}>
              Sign In
            </StyledLink>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))',
              '&:before': {
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))',
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={onClickAccount}>
          <ListItemIcon sx={{ color: 'text.primary' }}>
            <AccountCircleIcon />
          </ListItemIcon>
          Account Info
        </MenuItem>
        <MenuItem onClick={onClickColorMode}>
          <ListItemIcon sx={{ color: 'text.primary' }}>{isDarkMode ? <LightMode /> : <DarkMode />}</ListItemIcon>
          {isDarkMode ? 'Light' : 'Dark'} Mode
        </MenuItem>
        <MenuItem onClick={onClickLogoutInner}>
          <ListItemIcon sx={{ color: 'text.primary' }}>
            <LogoutIcon />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
      <Dialog open={signInOpen} onClose={onCloseDialog} maxWidth="xs">
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
            <ExitDialogButton onCloseDialog={onCloseDialog} />
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Typography variant="h4">Sign In to</Typography>
              <Typography variant="h4">Microgreen Manager</Typography>
            </Box>
            {/* <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body1">
                Log in with a social provider. We won't post anything anywhere. To access your account make sure to log
                in with the same provider.
              </Typography>
            </Box>
            <SocialButton
              disabled={loginIsLoading}
              onClick={fbLogin}
              icon={<FacebookIcon />}
              iconBgColor="rgb(24, 119, 242)"
              buttonBgColor="rgb(24, 119, 242)"
              label="Facebook"
            />
            <SocialButton
              disabled={loginIsLoading}
              onClick={googleLogin}
              icon={<GoogleIcon />}
              iconBgColor="#fff"
              buttonBgColor="rgb(66, 133, 244)"
              label="Google"
            /> */}
            {/* <Box sx={{ mb: 1, mt: 1 }}>
              <Divider sx={{ width: '300px', borderColor: (t) => t.custom.colors.common.darkBlack }} />
            </Box>
            <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
              <Typography variant="body1">Or log in with your email address.</Typography>
            </Box> */}
            <Box sx={{ width: '273px' }}>
              <TextField
                aria-describedby="email-input"
                id="email-input"
                label="Email"
                inputMode="email"
                value={email}
                type="email"
                onChange={onChangeEmail}
                onEnter={() => {
                  if (loading) {
                    return;
                  }
                  emailLogin();
                }}
                required
                disabled={loading}
                error={loginFailureMessage !== ''}
              />
            </Box>
            <Box sx={{ mt: 2, width: '273px' }}>
              <TextField
                aria-describedby="password-input"
                id="password-input"
                label="Password"
                value={password}
                type="password"
                onChange={onChangePassword}
                onEnter={() => {
                  if (loading) {
                    return;
                  }
                  emailLogin();
                }}
                required
                disabled={loading}
                hasSubtext
                subtext={loginFailureMessage}
                error={loginFailureMessage !== ''}
              />
            </Box>
            <Button
              sx={{ width: '273px', mt: 2 }}
              variant="contained"
              onClick={emailLogin}
              type="submit"
              disabled={loading}
            >
              Submit
            </Button>
            <Typography sx={{ mt: 2 }} variant="body2">
              Need to create an account?{' '}
              <Link
                onClick={() => setSignInOpen(false)}
                inlineText
                variant="body2"
                path={routes.SIGNUP}
                title="Sign up"
              />
              .
            </Typography>
            <Typography sx={{ mt: 0.5, mb: 2 }} variant="body2">
              Forgot your password?{' '}
              <Link
                onClick={() => setSignInOpen(false)}
                inlineText
                variant="body2"
                path={routes.FORGOT_PASSWORD}
                title="Reset your password"
              />
              .
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
